// Componentes básicos com Tailwind. Se o seu app usa shadcn/ui, pode trocar
// estes por Button/Input/Tabs/Dialog do shadcn sem mexer nas telas.
import React, { useEffect } from "react";
import { STATUS } from "../utils";

export function Campo({ rotulo, erro, dica, children, className = "" }) {
  return (
    <label className={`flex flex-col gap-1 text-sm ${className}`}>
      {rotulo && <span className="font-medium text-gray-700">{rotulo}</span>}
      {children}
      {dica && !erro && <span className="text-xs text-gray-500">{dica}</span>}
      {erro && <span className="text-xs text-red-600">{erro}</span>}
    </label>
  );
}

const base =
  "w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100 disabled:bg-gray-50";

export const Entrada = React.forwardRef(function Entrada(props, ref) {
  return <input ref={ref} {...props} className={`${base} ${props.className || ""}`} />;
});

export function Selecao({ opcoes, vazio, ...props }) {
  return (
    <select {...props} className={`${base} ${props.className || ""}`}>
      {vazio !== undefined && <option value="">{vazio}</option>}
      {opcoes.map(([v, r]) => (
        <option key={v} value={v}>
          {r}
        </option>
      ))}
    </select>
  );
}

export function Texto(props) {
  return <textarea rows={3} {...props} className={`${base} ${props.className || ""}`} />;
}

// ---- Campos numéricos no padrão brasileiro
const fmtBR = (n, casas) =>
  Number(n || 0).toLocaleString("pt-BR", { minimumFractionDigits: casas, maximumFractionDigits: casas });

// Ao entrar no campo, seleciona o conteúdo: o que for digitado substitui o valor (sem zero sobrando).
// O mouseup logo após o foco desfaria a seleção, por isso é ignorado uma vez.
const selecionarTudo = {
  onFocus: (e) => { e.target.select(); e.target.dataset.recemFocado = "1"; },
  onMouseUp: (e) => { if (e.target.dataset.recemFocado) { e.preventDefault(); delete e.target.dataset.recemFocado; } },
};

// Dinheiro estilo calculadora: digitar 1-0-0-0 vira 10,00. Sem zero sobrando,
// pontos e vírgula entram sozinhos. Ao entrar no campo, o valor fica selecionado.
export function Valor({ value, onChange, casas = 2, ...props }) {
  const texto = fmtBR(value, casas);
  const deDigitos = (dig) => {
    dig = dig.replace(/\D/g, "").replace(/^0+/, "").slice(0, 15);
    onChange(dig ? Number(dig) / 10 ** casas : 0);
  };
  const digitosAtuais = () => Math.round(Number(value || 0) * 10 ** casas).toString().replace(/^0+/, "");
  // Teclado: dígito entra sempre no final (como calculadora); Backspace apaga o último.
  const tecla = (e) => {
    const el = e.target;
    const tudo = el.selectionStart === 0 && el.selectionEnd === el.value.length && el.value.length > 0;
    if (/^\d$/.test(e.key)) {
      e.preventDefault();
      deDigitos((tudo ? "" : digitosAtuais()) + e.key);
    } else if (e.key === "Backspace" || e.key === "Delete") {
      e.preventDefault();
      deDigitos(tudo ? "" : digitosAtuais().slice(0, -1));
    }
  };
  return (
    <Entrada type="text" inputMode="numeric" value={texto} onKeyDown={tecla}
      onChange={(e) => deDigitos(e.target.value)} {...selecionarTudo}
      className="text-right" {...props} />
  );
}

// Número com casas decimais livres (quantidade, %, alíquotas): aceita vírgula ou ponto.
// Mantém o que a pessoa digita ("1," "0,5") e devolve número ("" se vazio).
export function Numero({ value, onChange, casas = 4, ...props }) {
  const paraTexto = (v) => (v === "" || v === null || v === undefined ? "" :
    Number(v).toLocaleString("pt-BR", { maximumFractionDigits: casas, useGrouping: false }));
  const [texto, setTexto] = React.useState(paraTexto(value));
  const ultimo = React.useRef(value);
  React.useEffect(() => {
    if (value !== ultimo.current) { setTexto(paraTexto(value)); ultimo.current = value; }
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps
  const mudar = (e) => {
    let t = e.target.value.replace(/[^\d.,]/g, "").replace(".", ",");
    const i = t.indexOf(",");
    if (i >= 0) t = t.slice(0, i + 1) + t.slice(i + 1).replace(/,/g, "").slice(0, casas);
    setTexto(t);
    const n = t === "" || t === "," ? "" : Number(t.replace(",", "."));
    ultimo.current = n;
    onChange(n);
  };
  return <Entrada type="text" inputMode="decimal" value={texto} onChange={mudar} {...selecionarTudo} {...props} />;
}

export function Botao({ variante = "primario", carregando, children, className = "", ...props }) {
  const v = {
    primario: "bg-blue-600 text-white hover:bg-blue-700",
    secundario: "border border-gray-300 bg-white text-gray-800 hover:bg-gray-50",
    perigo: "bg-red-600 text-white hover:bg-red-700",
    fantasma: "text-blue-700 hover:bg-blue-50",
  }[variante];
  return (
    <button
      type="button"
      {...props}
      disabled={carregando || props.disabled}
      className={`inline-flex items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-60 ${v} ${className}`}
    >
      {carregando && <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />}
      {children}
    </button>
  );
}

export function Abas({ abas, ativa, onChange }) {
  return (
    <div className="flex gap-1 overflow-x-auto border-b border-gray-200">
      {abas.map(([id, rotulo, alerta]) => (
        <button
          type="button"
          key={id}
          onClick={() => onChange(id)}
          className={`whitespace-nowrap border-b-2 px-4 py-2 text-sm font-medium ${
            ativa === id ? "border-blue-600 text-blue-700" : "border-transparent text-gray-600 hover:text-gray-900"
          }`}
        >
          {rotulo}
          {alerta && <span className="ml-1 inline-block h-2 w-2 rounded-full bg-red-500" />}
        </button>
      ))}
    </div>
  );
}

export function Modal({ aberto, titulo, onFechar, children, largura = "max-w-lg" }) {
  useEffect(() => {
    if (!aberto) return undefined;
    const esc = (e) => e.key === "Escape" && onFechar();
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [aberto, onFechar]);
  if (!aberto) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onMouseDown={onFechar}>
      <div
        role="dialog"
        aria-modal="true"
        className={`max-h-[90vh] w-full ${largura} overflow-y-auto rounded-lg bg-white shadow-xl`}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b px-5 py-3 border-gray-200">
          <h2 className="text-base font-semibold">{titulo}</h2>
          <button type="button" onClick={onFechar} className="text-gray-500 hover:text-gray-800" aria-label="Fechar">
            ✕
          </button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

export function StatusNota({ status }) {
  const s = STATUS[status] || { rotulo: status, cor: "bg-gray-100 text-gray-700" };
  return <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${s.cor}`}>{s.rotulo}</span>;
}

export function Aviso({ tipo = "info", children }) {
  const cor = {
    info: "border-blue-200 bg-blue-50 text-blue-900",
    erro: "border-red-200 bg-red-50 text-red-900",
    sucesso: "border-emerald-200 bg-emerald-50 text-emerald-900",
    atencao: "border-amber-200 bg-amber-50 text-amber-900",
  }[tipo];
  return <div className={`whitespace-pre-line rounded-md border px-4 py-3 text-sm ${cor}`}>{children}</div>;
}

export function Cartao({ titulo, acoes, children, className = "" }) {
  return (
    <section className={`rounded-lg border border-gray-200 bg-white ${className}`}>
      {(titulo || acoes) && (
        <header className="flex items-center justify-between border-b px-4 py-3 border-gray-200">
          <h3 className="text-sm font-semibold text-gray-800">{titulo}</h3>
          <div className="flex gap-2">{acoes}</div>
        </header>
      )}
      <div className="p-4">{children}</div>
    </section>
  );
}

export function Tabela({ colunas, linhas, vazio = "Nada encontrado", chave = (l, i) => i }) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
          <tr>
            {colunas.map((c) => (
              <th key={c.id} className={`px-3 py-2 ${c.direita ? "text-right" : ""}`}>
                {c.rotulo}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {linhas.length === 0 && (
            <tr>
              <td colSpan={colunas.length} className="px-3 py-6 text-center text-gray-500">
                {vazio}
              </td>
            </tr>
          )}
          {linhas.map((l, i) => (
            <tr key={chave(l, i)} className="hover:bg-gray-50">
              {colunas.map((c) => (
                <td key={c.id} className={`px-3 py-2 ${c.direita ? "text-right tabular-nums" : ""}`}>
                  {c.render ? c.render(l) : l[c.id]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
